import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import moment from 'moment';
import { Button, Pagination, Row, Col, Form } from 'react-bootstrap';
import Confirm from '../../../components/Modal/Confirm';

import Loading from '../../../components/Loading/Loading';
import throttle from '../../../utils/throttle';
import pagination from '../../../utils/pagination';
import { getAiPrompts, updateAiPrompt } from '../AiToolsActions';
import PromptConfig from './PromptConfig';
import { PRICES, getModel } from '../../../utils/aiUtils';

import addIcon from '../../../assets/images/add-circle.svg';
import activeIcon from '../../../assets/images/active-icon.svg';
import viewIcon from '../../../assets/images/view-icon.svg';

import '../../../assets/styles/_table.scss';
import './PromptsTable.scss';

class PromptsTable extends Component {
  static propTypes = {
    pagination: PropTypes.bool,
    show: PropTypes.number,
  };

  static defaultProps = {
    pagination: true,
    show: 10,
  };

  constructor(props) {
    super(props);

    this.state = {
      page: 1,
      dateFormat: 'DD MMM YYYY HH:mm',
      search: '',
    };

    this.getPromptsThrottled = throttle(this.getPrompts, 500, this);
  }

  componentDidMount() {
    this.getPrompts();
  }

  handleChange = (ev) => {
    this.setState({ search: ev.target.value, page: 1 });
    this.getPromptsThrottled();
  };

  goto = (page = 1) => {
    if (page < 1) {
      page = 1;
    }

    this.setState({ page }, this.getPrompts);
  };

  getPrompts = () => {
    const { search, page } = this.state;
    this.props.getAiPrompts({
      search,
      page,
    });
  };

  truncateText = (text, maxLength = 200) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  toggleActive = (id) => {
    this.props.updateAiPrompt(id, { active: true });
  };

  calculateStats = (data) => {
    if (!data.stats) {
      return {
        cacheHitRate: 'N/A',
        successRate: 'N/A',
        totalRequests: 0,
        totalCost: '$0.00',
        avgCostPerRequest: '$0.0000',
        totalTime: '0s',
        avgTimePerRequest: '0.000s',
      };
    }

    const stats = data.stats;
    const { llmModel } = getModel(data.template);
    const pricing = PRICES[llmModel] || PRICES['gpt-4o'];

    const cacheHitRate = stats.total > 0 ? ((stats.cacheHits / stats.total) * 100).toFixed(1) + '%' : '0.0%';
    const successRate = stats.requests > 0 ? ((stats.successCount / stats.requests) * 100).toFixed(1) + '%' : '0.0%';

    const totalCost = (
      (stats.totalInputTokens / 1000000) * pricing.input +
      (stats.totalOutputTokens / 1000000) * pricing.output
    );
    const avgCostPerRequest = stats.requests > 0 ? totalCost / stats.requests : 0;

    const totalTime = (stats.totalTime).toFixed(0) + 's';
    const avgTimePerRequest = stats.requests > 0 ? (stats.totalTime / stats.requests).toFixed(3) + 's' : '0.000s';

    return {
      cacheHitRate,
      successRate,
      totalRequests: stats.requests,
      totalCost: '$' + totalCost.toFixed(2),
      avgCostPerRequest: '$' + avgCostPerRequest.toFixed(4),
      totalTime,
      avgTimePerRequest,
    };
  };

  renderPagination = () => {
    const pages = pagination(this.props.total, this.props.page, 10, 5);

    if (pages.totalPages < 2) {
      return <div></div>;
    }
    return (
      <Pagination>
        {pages.currentPage > 1 && <Pagination.First onClick={() => this.goto(1)} />}
        {pages.currentPage > 1 && <Pagination.Prev onClick={() => this.goto(pages.currentPage - 1)} />}
        {pages.pages.map((p, i) => {
          return (
            <Pagination.Item key={i} active={p === pages.currentPage} onClick={() => this.goto(p)}>
              {p}
            </Pagination.Item>
          );
        })}
        {pages.currentPage < pages.totalPages && <Pagination.Next onClick={() => this.goto(pages.currentPage + 1)} />}
        {pages.currentPage < pages.totalPages && <Pagination.Last onClick={() => this.goto(pages.totalPages)} />}
      </Pagination>
    );
  };

  renderRow = (data, i) => {
    const stats = this.calculateStats(data);
    const { dateFormat } = this.state;

    return (
      <Row key={i} className='row-item'>
        {/* Col 1 - Name, Prompt and Template */}
        <Col xs={12} md={4} className='col-one'>
          <p>
            <label>Name:</label>
            <span>{data.name}</span>
          </p>
          <p>
            <label>Created on:</label>
            <span>{moment(data.createdAt).format(dateFormat)}</span>
          </p>
          <p>
            <label>Status:</label>
            <span className={`status ${data.active ? 'green' : 'red'}`}>
              {data.active ? 'ACTIVE' : 'INACTIVE'}
            </span>
          </p>
        </Col>
        <Col xs={12} md={5} className='col-two'>
          <p>
            <label className='d-block'>Prompt:</label>
            <span>{this.truncateText(data.prompt)}</span>
          </p>
          
          <p>
            <label className='d-block'>Template:</label>
            <span>{this.truncateText(data.template)}</span>
          </p>
        </Col>

        {/* Col 4 - Statistics */}
        <Col xs={12} md={1} className='col-four'>
          <p className='stats-block'>
            <p className='stat-item'>
              <label>Cache Hit Rate:</label> <span>{stats.cacheHitRate}</span>
            </p>
            <p className='stat-item'>
              <label>Success Rate:</label> <span>{stats.successRate}</span>
            </p>
            <p className='stat-item'>
              <label>Total Requests:</label> <span>{stats.totalRequests}</span>
            </p>
            <p className='stat-item'>
              <label>Total Cost:</label> <span>{stats.totalCost}</span>
            </p>
            <p className='stat-item'>
              <label>Avg Cost/Req:</label> <span>{stats.avgCostPerRequest}</span>
            </p>
            <p className='stat-item'>
              <label>Total Time:</label> <span>{stats.totalTime}</span>
            </p>
            <p className='stat-item'>
              <label>Avg Time/Req:</label> <span>{stats.avgTimePerRequest}</span>
            </p>
          </p>
        </Col>

        {/* Col 5 - Actions Dropdown */}
        <Col className='col-five' style={{ maxWidth: 'none'}}>
          <div className='d-flex flex-wrap gap-2'>
            <PromptConfig model={data} readOnly={true} onClose={this.getPrompts}>
              <Button variant='secondary' size='md' className='btn-icon'><img src={viewIcon} alt="View"/></Button>
            </PromptConfig>
            {!data.active && (
              <Confirm
                body={
                  <p>
                    Are you sure you want to activate <br />
                    this prompt?
                  </p>
                }
                confirmText="Activate"
                icon={activeIcon}
                title="Activate this prompt"
                onConfirm={() => this.toggleActive(data._id)}
              >
                <Button variant='secondary' size='md' className='btn-icon'><img src={activeIcon} alt="Activate"/></Button>
              </Confirm>
            )}
          </div>
        </Col>
      </Row>
    );
  };

  render() {
    const { isLoading, results, total } = this.props;

    return (
      <div>
        <div className='table-content'>
          <div className='table-filter'>
            <h2>Prompts</h2>
            <div className='filter-content' id="filter-content">
              <PromptConfig onClose={this.getPrompts}>
                <Button size='md'>
                  <img src={addIcon} alt="Add partner"/> New Prompt
                </Button>
              </PromptConfig>
              <Form.Group controlId="Search" className="search-form">
                <Form.Control
                  type="text"
                  name="search"
                  placeholder="Search"
                  onChange={this.handleChange}
                  value={this.state.search}
                />
              </Form.Group>
            </div>
            
          </div>

          {isLoading && (
            <div className='table-loading'>
              <Loading />
            </div>
          )}
          {!isLoading && total === 0 && (
            <div className='no_data'>
              <p> No data available </p>
            </div>
          )}

          <div className='table-list'>
            {!isLoading && results && _.map(results, (data, i) => this.renderRow(data, i))}
          </div>
        </div>
        {total > 0 && this.renderPagination()}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    getAiPrompts: (opt) => dispatch(getAiPrompts(opt)),
    updateAiPrompt: (id, model) => dispatch(updateAiPrompt(id, model)),
  };
};

function mapStateToProps(state) {
  return {
    results: state.aiTools.results,
    isLoading: state.aiTools.isLoading,
    page: state.aiTools.page,
    total: state.aiTools.total,
    user: state.auth.user,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(PromptsTable);
